/* In-memory implementation of AnalyticsClient. Deterministic, contract-shaped,
 * and self-consistent across endpoints (every method projects from the same
 * buildModel(query) result). A small artificial latency makes loading states
 * feel real in the app; tests construct it with delayMs: 0. */

import type { AnalyticsClient } from "../AnalyticsClient";
import type {
  AgentRollup,
  AgentsResponse,
  AnalyticsQuery,
  ApiError,
  AppliedFilters,
  Envelope,
  ErrorBreakdownResponse,
  ErrorsQuery,
  ErrorsResponse,
  Meta,
  ModelRollup,
  ModelsResponse,
  SessionDetailResponse,
  SessionsQuery,
  SessionsResponse,
  SummaryResponse,
  TransformersResponse,
  TrendBucket,
  TrendsResponse,
} from "../types";
import {
  AGENTS,
  CONTEXT_BREAKDOWN_SINCE,
  MOCK_DATA_THROUGH,
  MOCK_NOW,
  ORG_STRUCTURE,
  PRICING_VERSION,
  UNREGISTERED_AGENTS,
} from "./catalog";
import { buildModel, resolveQuery, round2, type ResolvedQuery } from "./derive";
import {
  buildSessionDetail,
  buildSessionList,
  buildTrends,
  scaleCost,
} from "./sessions";
import {
  buildErrorBreakdown,
  buildErrorList,
  failedCount,
  failedCountForAgent,
} from "./errors";
import { splitByWeights } from "./seed";
import { UNTAGGED_AGENT } from "../errorInsights";

function delay(ms: number): Promise<void> {
  return ms <= 0 ? Promise.resolve() : new Promise((r) => setTimeout(r, ms));
}

/** The synthetic `(untagged)` roster row: the traffic that carried no
 *  x-agent-id, folded into one row so per-agent failures still sum to
 *  /summary.failed_calls (OPSS-352). Shaped as the BFF emits it — no registry
 *  entry, so a null name and an empty kind. Only failures are drawn untagged
 *  here, so those are the row's whole traffic and it carries no spend. */
function untaggedRollup(failed: number): AgentRollup {
  return {
    agent_id: UNTAGGED_AGENT,
    agent_kind: "",
    name: null,
    call_count: failed,
    failed_calls: failed,
    session_count: 0,
    cost: { input: 0, cache_read: 0, cache_write: 0, output: 0, total: 0 },
    tokens: { input: 0, output: 0, cache_read: 0, cache_write: 0 },
    bytes_saved_pct: 0,
    savings_usd: 0,
    routing: { routed_calls: 0 },
    models: [],
  };
}

function appliedFilters(r: ResolvedQuery): AppliedFilters {
  const f: AppliedFilters = {};
  // Echo repo only when it was actually applied (not the expanded default).
  if (r.reposExplicit) f.repo = r.repos;

  if (r.models.length) f.model = r.models;

  if (r.configs.length) f.config_id = r.configs;

  if (r.experiments.length) f.experiment = r.experiments;

  if (r.sessionIds.length) f.session_id = r.sessionIds;

  if (r.routed !== undefined) f.routed = r.routed;

  if (r.agentIds.length) f.agent_id = r.agentIds;

  if (r.agentKinds.length) f.agent_kind = r.agentKinds;

  return f;
}

function buildMeta(r: ResolvedQuery): Meta {
  const partial = new Date(r.fromMs) < new Date(CONTEXT_BREAKDOWN_SINCE);
  return {
    generated_at: MOCK_NOW.toISOString(),
    data_through: MOCK_DATA_THROUGH.toISOString(),
    window: {
      from: new Date(r.fromMs).toISOString(),
      to: new Date(r.toMs).toISOString(),
    },
    pricing_version: PRICING_VERSION,
    filters: appliedFilters(r),
    data_availability: {
      context_breakdown_since: CONTEXT_BREAKDOWN_SINCE,
      context_breakdown_partial: partial,
    },
  };
}

export interface MockClientOptions {
  /** Simulated latency per call (ms). Default 220; pass 0 in tests. */
  delayMs?: number;
}

export class MockAnalyticsClient implements AnalyticsClient {
  private readonly delayMs: number;

  constructor(opts: MockClientOptions = {}) {
    this.delayMs = opts.delayMs ?? 220;
  }

  private async respond<T>(data: T, r: ResolvedQuery): Promise<Envelope<T>> {
    await delay(this.delayMs);
    return { data, meta: buildMeta(r) };
  }

  async getSummary(query: AnalyticsQuery): Promise<SummaryResponse> {
    const r = resolveQuery(query);
    const m = buildModel(r);
    const failed = failedCount(r, m.callCount);
    return this.respond(
      {
        call_count: m.callCount,
        session_count: m.sessionCount,
        failed_calls: failed,
        error_rate: m.callCount > 0 ? failed / m.callCount : 0,
        cost: m.cost,
        tokens: m.tokens,
        bytes: {
          before: m.bytesBefore,
          after: m.bytesAfter,
          saved: m.bytesBefore - m.bytesAfter,
          saved_pct: Math.round(m.savedPct * 1000) / 10,
        },
        tokens_est: {
          before: Math.round(m.bytesBefore / 4),
          after: Math.round(m.bytesAfter / 4),
          saved: Math.round((m.bytesBefore - m.bytesAfter) / 4),
          saved_pct: Math.round(m.savedPct * 1000) / 10,
        },
        cache: m.cache,
        models: m.models,
        routing: m.routing,
        latency_ms: m.latency,
        // Byte-derived savings headline (the real BFF sends this; here we reuse
        // the block-clawback sum so mock and real agree).
        savings_usd: m.savedUsd,
        context_blocks: m.blocks,
      },
      r,
    );
  }

  async getSessions(query: SessionsQuery): Promise<SessionsResponse> {
    const r = resolveQuery(query);
    const m = buildModel(r);
    const list = buildSessionList(
      r,
      {
        limit: Math.min(Math.max(query.limit ?? 50, 1), 500),
        cursor: query.cursor,
        sort: query.sort ?? "cost_usd",
        order: query.order ?? "desc",
      },
      m.sessionCount,
    );
    return this.respond(
      { items: list.items, next_cursor: list.next_cursor, total: list.total },
      r,
    );
  }

  async getErrors(query: ErrorsQuery): Promise<ErrorsResponse> {
    const r = resolveQuery(query);
    const m = buildModel(r);
    const list = buildErrorList(
      r,
      {
        limit: Math.min(Math.max(query.limit ?? 50, 1), 500),
        cursor: query.cursor,
        status: query.status,
        provider: query.provider,
      },
      m.sessionCount,
    );
    return this.respond(
      { items: list.items, next_cursor: list.next_cursor, total: list.total },
      r,
    );
  }

  async getErrorBreakdown(query: ErrorsQuery): Promise<ErrorBreakdownResponse> {
    const r = resolveQuery(query);
    return this.respond(
      buildErrorBreakdown(r, {
        status: query.status,
        provider: query.provider,
      }),
      r,
    );
  }

  async getSession(
    sessionId: string,
    query: AnalyticsQuery = {},
  ): Promise<SessionDetailResponse> {
    const r = resolveQuery(query);
    const m = buildModel(r);
    const detail = buildSessionDetail(r, sessionId, m.sessionCount);
    if (!detail) {
      // Non-2xx bodies are { error: ApiError } per contract §2.
      const error: ApiError = {
        code: "not_found",
        message: `Unknown session ${sessionId}`,
      };
      throw { error };
    }

    return this.respond(detail, r);
  }

  async getTransformers(query: AnalyticsQuery): Promise<TransformersResponse> {
    const r = resolveQuery(query);
    const m = buildModel(r);
    return this.respond({ items: m.transformers }, r);
  }

  async getTrends(
    query: AnalyticsQuery & { bucket?: TrendBucket },
  ): Promise<TrendsResponse> {
    const r = resolveQuery(query);
    const m = buildModel(r);
    // Contract §6: bucket defaults to "day". Callers pick "hour" explicitly.
    const bucket: TrendBucket = query.bucket ?? "day";
    const points = buildTrends(r, bucket, {
      callCount: m.callCount,
      cost: m.cost,
    });
    return this.respond({ points }, r);
  }

  async getAgents(query: AnalyticsQuery): Promise<AgentsResponse> {
    const r = resolveQuery(query);
    // One rollup per agent that survives the agent_id / agent_kind filters,
    // each derived from its own per-agent model so it ties out with
    // /summary?agent_id=<id>. Unregistered slices carry a null name.
    const universe = [
      ...AGENTS.map((a) => ({
        agent_id: a.agent_id,
        name: a.name as string | null,
        kind: a.kind as string,
      })),
      ...UNREGISTERED_AGENTS.map((a) => ({
        agent_id: a.agent_id,
        name: null as string | null,
        kind: a.kind as string,
      })),
    ];
    const visible = universe.filter(
      (a) =>
        (!r.agentIds.length || r.agentIds.includes(a.agent_id)) &&
        (!r.agentKinds.length || r.agentKinds.includes(a.kind)),
    );

    const items: AgentRollup[] = visible
      .map((a) => {
        const scoped = resolveQuery({
          ...query,
          agent_id: [a.agent_id],
          agent_kind: undefined,
        });
        const m = buildModel(scoped);
        // Token split for this agent's per-model breakdown (OPSS-421), by call
        // share. Computed once per agent, outside the models map — splitByWeights
        // returns the whole vector.
        const callW = m.models.map((mm) => mm.calls);
        const tok = {
          input: splitByWeights(m.tokens.input, callW, true),
          output: splitByWeights(m.tokens.output, callW, true),
          cache_read: splitByWeights(m.tokens.cache_read, callW, true),
          cache_write: splitByWeights(m.tokens.cache_write, callW, true),
        };
        // Savings split by spend share, so the per-model saved figures sum to the
        // agent's own — the real BFF sums this agent's stored per-model savings.
        const savedByModel = splitByWeights(
          m.savedUsd,
          m.models.map((mm) => mm.cost_usd),
        );
        return {
          agent_id: a.agent_id,
          agent_kind: a.kind,
          name: a.name,
          call_count: m.callCount,
          // Counted off the window's single failure draw, not re-derived from
          // this agent's own call volume: a second derivation drifts, and the
          // agent card would then contradict the workspace drill-down that
          // opens from it (OPSS-352).
          failed_calls: failedCountForAgent(r, a.agent_id),
          session_count: m.sessionCount,
          cost: m.cost,
          tokens: m.tokens,
          bytes_saved_pct: Math.round(m.savedPct * 1000) / 10,
          savings_usd: m.savedUsd,
          routing: { routed_calls: m.routing.routed_calls },
          // cost_usd/tokens back the agent detail page's per-model breakdown.
          // Split from this agent's own totals so the breakdown sums to the
          // agent's spend, as it does on the real BFF.
          models: m.models.map((mm, i) => ({
            name: mm.name,
            calls: mm.calls,
            cost_usd: mm.cost_usd,
            tokens: {
              input: tok.input[i],
              output: tok.output[i],
              cache_read: tok.cache_read[i],
              cache_write: tok.cache_write[i],
            },
            savings_usd: round2(savedByModel[i]),
          })),
        };
      })
      .sort((a, b) => b.cost.total - a.cost.total);

    // ~65% of the window's failures carry no agent_id. Left off the roster they
    // are counted by /summary and by nothing else, which is the under-reporting
    // OPSS-352 closes — and every UI path built for the synthetic row would be
    // dead in mock mode. A kind filter can't match a kind-less bucket.
    const untagged =
      (!r.agentIds.length || r.agentIds.includes(UNTAGGED_AGENT)) &&
      !r.agentKinds.length
        ? failedCountForAgent(r, UNTAGGED_AGENT)
        : 0;
    // Cost-sorted, and it has none — so it belongs after every real agent.
    if (untagged > 0) items.push(untaggedRollup(untagged));

    return this.respond({ items }, r);
  }

  async getModels(query: AnalyticsQuery): Promise<ModelsResponse> {
    const r = resolveQuery(query);
    // Projected from ONE buildModel result rather than re-derived per model (the
    // shape getAgents uses). Two reasons: buildModel's per-model split already
    // exists as m.models, and a per-row re-derivation re-jitters each row, so the
    // rows would not add up to the spend tile sitting directly above this section
    // on the same screen. Splitting the window totals by weight keeps the parts
    // summing to the whole exactly, which is what a reader checks by eye.
    //
    // Nothing here draws from buildModel's rng: every helper below is pure. A new
    // rng call inside buildModel would shift every downstream mock number.
    const m = buildModel(r);
    const callW = m.models.map((mm) => mm.calls);
    const costW = m.models.map((mm) => mm.cost_usd);
    const failed = splitByWeights(failedCount(r, m.callCount), callW, true);
    const input = splitByWeights(m.tokens.input, callW, true);
    const output = splitByWeights(m.tokens.output, callW, true);
    const cacheRead = splitByWeights(m.tokens.cache_read, callW, true);
    const cacheWrite = splitByWeights(m.tokens.cache_write, callW, true);
    const saved = splitByWeights(m.savedUsd, costW);

    const items: ModelRollup[] = m.models
      .map((mm, i) => ({
        model: mm.name,
        call_count: mm.calls,
        failed_calls: failed[i],
        cost: scaleCost(m.cost, mm.cost_usd),
        tokens: {
          input: input[i],
          output: output[i],
          cache_read: cacheRead[i],
          cache_write: cacheWrite[i],
        },
        // The byte delta is a window-level property in the mock; every model
        // shares it rather than inventing a per-model compression rate.
        bytes_saved_pct: Math.round(m.savedPct * 1000) / 10,
        savings_usd: round2(saved[i]),
      }))
      .sort((a, b) => b.cost.total - a.cost.total);

    return this.respond({ items }, r);
  }

  async getOrgStructure() {
    await delay(this.delayMs);
    return ORG_STRUCTURE;
  }
}
